// src/pages/business/posts/BusinessPosts.tsx
// Web adaptation of mobile BusinessPostsScreen - Create and manage business posts

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Plus, Image, Video, Heart, MessageCircle, Share2,
  MoreVertical, Trash2, Edit, Loader2, Search, Calendar, User,
  XCircle, Send
} from 'lucide-react';
import apiClient from '@/services/apiClient';
import { useToast } from '@/contexts/ToastContext';
import { getImageUrl } from '@/config/media';

interface BusinessPost {
  id: string;
  business_id: string;
  content: string;
  media_urls?: string[];
  likes_count: number;
  comments_count: number;
  shares_count: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  business_name?: string;
  business_logo?: string;
}

const BusinessPosts: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();
  const { success: toastSuccess, error: toastError } = useToast();

  const [posts, setPosts] = useState<BusinessPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newContent, setNewContent] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchPosts();
  }, [businessId]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get(`/businesses/${businessId}/posts`);
      setPosts(res.data.posts || []);
    } catch (error: any) {
      console.error('Failed to fetch posts:', error);
      toastError('Failed to load posts');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const onRefresh = () => {
    setRefreshing(true);
    fetchPosts();
  };

  const handleCreatePost = async () => {
    if (!newContent.trim()) {
      toastError('Please enter post content');
      return;
    }

    setCreating(true);
    try {
      await apiClient.post(`/businesses/${businessId}/posts`, {
        content: newContent.trim(),
      });
      toastSuccess('Post created successfully');
      closeCreateModal();
      fetchPosts();
    } catch (error: any) {
      toastError('Failed to create post');
    } finally {
      setCreating(false);
    }
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setNewContent('');
  };

  const deletePost = async (postId: string) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    try {
      await apiClient.delete(`/businesses/${businessId}/posts/${postId}`);
      toastSuccess('Post deleted');
      fetchPosts();
    } catch (error: any) {
      toastError('Failed to delete post');
    }
  };

  const togglePostStatus = async (postId: string, currentStatus: boolean) => {
    try {
      await apiClient.patch(`/businesses/${businessId}/posts/${postId}`, {
        is_active: !currentStatus,
      });
      fetchPosts();
      toastSuccess(`Post ${currentStatus ? 'disabled' : 'enabled'}`);
    } catch (error: any) {
      toastError('Failed to update post');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const filteredPosts = posts.filter((p) =>
    p.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-light-bg dark:bg-dark-bg">
        <div className="sticky top-0 z-40 bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(`/business/${businessId}`)} className="flex-shrink-0">
            <ArrowLeft size={24} className="text-primary" />
          </button>
          <h1 className="flex-1 font-bold text-light-text dark:text-dark-text" style={{ fontSize: 20 }}>
            Business Posts
          </h1>
        </div>
        <div className="flex items-center justify-center py-24">
          <Loader2 size={40} className="animate-spin mx-auto text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-light-bg dark:bg-dark-bg pb-24">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate(`/business/${businessId}`)} className="flex-shrink-0">
          <ArrowLeft size={24} className="text-primary" />
        </button>
        <h1 className="flex-1 font-bold text-light-text dark:text-dark-text" style={{ fontSize: 20 }}>
          Business Posts
        </h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-white"
          style={{ backgroundColor: '#4F46E5' }}
        >
          <Plus size={16} />
          New
        </button>
      </div>

      <div className="max-w-5xl mx-auto px-4 pt-4">
        {/* Search */}
        <div className="relative mb-4">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-light-subtle dark:text-dark-subtle" />
          <input
            type="text"
            placeholder="Search posts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 rounded-xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border text-sm text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>

        {/* Posts List */}
        {filteredPosts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center gap-4">
            <Image size={64} className="text-light-subtle dark:text-dark-subtle" />
            <p className="font-semibold text-light-text dark:text-dark-text" style={{ fontSize: 18 }}>
              No Posts Yet
            </p>
            <p className="text-sm text-light-subtle dark:text-dark-subtle">
              {searchQuery ? 'Try adjusting your search' : 'Create your first post to engage with customers'}
            </p>
            {!searchQuery && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="mt-2 px-6 py-3 rounded-xl text-white font-semibold"
                style={{ backgroundColor: '#4F46E5' }}
              >
                Create Post
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredPosts.map((post) => (
              <div
                key={post.id}
                className="bg-light-card dark:bg-dark-card rounded-xl p-4 border border-light-border dark:border-dark-border shadow-sm"
              >
                {/* Post Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <User size={20} style={{ color: '#4F46E5' }} />
                    </div>
                    <div>
                      <p className="font-bold text-sm text-light-text dark:text-dark-text">
                        {post.business_name || 'Your Business'}
                      </p>
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-light-subtle dark:text-dark-subtle">
                          {formatDate(post.created_at)}
                        </p>
                        <span
                          className={`px-2 py-0.5 rounded text-xs font-semibold ${
                            post.is_active
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                              : 'bg-gray-100 dark:bg-gray-900/30 text-gray-600 dark:text-gray-400'
                          }`}
                        >
                          {post.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => togglePostStatus(post.id, post.is_active)}
                      className="p-2 rounded-lg hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
                    >
                      {post.is_active ? (
                        <XCircle size={16} className="text-light-subtle dark:text-dark-subtle" />
                      ) : (
                        <Send size={16} className="text-green-500" />
                      )}
                    </button>
                    <button
                      onClick={() => deletePost(post.id)}
                      className="p-2 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                {/* Post Content */}
                <p className="text-sm text-light-text dark:text-dark-text mb-3 whitespace-pre-wrap">
                  {post.content}
                </p>

                {/* Post Media */}
                {post.media_urls && post.media_urls.length > 0 && (
                  <div className="mb-3 rounded-lg overflow-hidden">
                    <img
                      src={getImageUrl(post.media_urls[0])}
                      alt="Post media"
                      className="w-full h-48 object-cover"
                    />
                  </div>
                )}

                {/* Post Stats */}
                <div className="flex items-center gap-4 pt-3 border-t border-light-border dark:border-dark-border">
                  <div className="flex items-center gap-1 text-light-subtle dark:text-dark-subtle">
                    <Heart size={14} />
                    <span className="text-xs">{post.likes_count}</span>
                  </div>
                  <div className="flex items-center gap-1 text-light-subtle dark:text-dark-subtle">
                    <MessageCircle size={14} />
                    <span className="text-xs">{post.comments_count}</span>
                  </div>
                  <div className="flex items-center gap-1 text-light-subtle dark:text-dark-subtle">
                    <Share2 size={14} />
                    <span className="text-xs">{post.shares_count}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Refresh Button */}
      <button
        onClick={onRefresh}
        disabled={refreshing}
        className="fixed bottom-6 right-6 w-14 h-14 rounded-full flex items-center justify-center shadow-lg z-50"
        style={{ backgroundColor: '#4F46E5' }}
      >
        <Loader2 size={24} className={`text-white ${refreshing ? 'animate-spin' : ''}`} />
      </button>

      {/* Create Post Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-light-card dark:bg-dark-card rounded-2xl w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b border-light-border dark:border-dark-border">
              <h2 className="text-lg font-bold text-light-text dark:text-dark-text">Create Post</h2>
              <button
                onClick={closeCreateModal}
                className="p-2 rounded-lg hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
              >
                <XCircle size={20} className="text-light-subtle dark:text-dark-subtle" />
              </button>
            </div>

            <div className="p-6">
              <textarea
                value={newContent}
                onChange={(e) => setNewContent(e.target.value)}
                placeholder="What's on your mind? Share updates, offers, or news with your customers..."
                rows={5}
                className="w-full px-4 py-3 rounded-xl bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border text-sm text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
              />
              <div className="flex items-center justify-between mt-4">
                <div className="flex items-center gap-2">
                  <button className="p-2 rounded-lg hover:bg-light-bg dark:hover:bg-dark-bg transition-colors">
                    <Image size={18} className="text-light-subtle dark:text-dark-subtle" />
                  </button>
                  <button className="p-2 rounded-lg hover:bg-light-bg dark:hover:bg-dark-bg transition-colors">
                    <Video size={18} className="text-light-subtle dark:text-dark-subtle" />
                  </button>
                </div>
                <button
                  onClick={handleCreatePost}
                  disabled={creating || !newContent.trim()}
                  className="flex items-center gap-2 px-6 py-2 rounded-xl text-white font-semibold transition-all hover:opacity-90 disabled:opacity-50"
                  style={{ backgroundColor: '#4F46E5' }}
                >
                  {creating ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Posting...
                    </>
                  ) : (
                    <>
                      <Send size={16} />
                      Post
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessPosts;